import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { LocalStorageService } from "../../services/local-storage.service";
import { RouterService } from "../../services/router.service";
import { CourtReservation } from "../../model/court-reservation";
import { User } from "../../model/user";
import { Court } from "../../model/court";
import { ActivatedRoute } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { CustomNotificationService } from "../../services/custom-notification.service";
import { CustomModalService } from "../../services/custom-modal.service";
import { CourtService } from "../../services/court.service";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";
import { UserService } from "../../services/user.service";
import { PlayerService } from "../../services/player.service";

@Component({
  selector: "app-confirm-match",
  standalone: true,
  imports: [FormsModule, CommonModule, LucideAngularModule],
  templateUrl: "./confirm-match.component.html",
  styleUrl: "./confirm-match.component.css",
})
export class ConfirmMatchComponent implements OnInit, OnChanges {
  constructor(
    private localStorageService: LocalStorageService,
    private routerService: RouterService,
    private authenticationService: AuthenticationService,
    private courtService: CourtService,
    private courtReservationService: CourtReservationService,
    private route: ActivatedRoute,
    private userService: UserService,
    private playerService: PlayerService,
    private notificationService: CustomNotificationService,
    private modalService: CustomModalService,
  ) {}

  @Input() courtReservation!: CourtReservation;
  courtReservations!: CourtReservation[];
  users!: User[];
  selectedMatchType: string = "single";
  courts!: Court[];
  @Input() yourMatchComponent: boolean = false;
  matchStarted: boolean = false;
  points: string[] = ["+", "+", "+", "+", "+", "+"];
  currentUser: User | null = this.authenticationService.currentUserValue;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["courtReservation"] && this.courtReservation) {
      this.initMatch();
      this.refreshAndFinalizeIfApproved();
    }
  }

  async ngOnInit() {
    this.courtReservations =
      (await this.localStorageService.getItem("courtReservations")) || [];
    this.courts = (await this.localStorageService.getItem("courts")) || [];
    this.users = (await this.localStorageService.getItem("users")) || [];

    if (
      !this.yourMatchComponent &&
      this.route.toString().includes("your-match")
    ) {
      this.yourMatchComponent = true;
      if (!this.courtReservation) {
        const id = Number(this.route.snapshot.paramMap.get("id"));
        if (!isNaN(id) && this.currentUser?.id) {
          const start = new Date(2000, 0, 1);
          const end = new Date(2100, 0, 1);
          this.courtReservationService
            .getCourtReservationsForUser(this.currentUser.id, start, end)
            .subscribe((reservations: any[]) => {
              const match = reservations.find((r: any) => r.id === id);
              if (match) {
                const startDt = new Date(match.reservation_start_time);
                const players = (match.courtReservationPlayer || []).sort(
                  (a: any, b: any) => a.position - b.position,
                );
                this.courtReservation = {
                  ...match,
                  date: startDt.toISOString().split("T")[0],
                  time: startDt.getHours(),
                  player_ids: players.map((p: any) => p.player_id),
                  courtReservationPlayer: players,
                } as CourtReservation;
                this.initMatch();
                this.refreshAndFinalizeIfApproved();
              }
            });
          return;
        }
      }
    } else if (!this.courtReservation) {
      this.courtReservation = await this.localStorageService.getItem(
        "courtReservation",
      );
    }
    this.initMatch();
    this.refreshAndFinalizeIfApproved();
  }

  private initMatch() {
    if (!this.courtReservation) return;
    this.selectedMatchType =
      this.courtReservation.player_ids.length == 2 ? "single" : "double";

    if (
      !this.courtReservation.sets ||
      this.courtReservation.sets.length === 0
    ) {
      this.courtReservation.sets = [-1, -1, -1, -1, -1, -1];
    }

    for (let i = 0; i < 6; i++) {
      if (i < this.courtReservation.sets.length) {
        this.points[i] =
          this.courtReservation.sets[i] === -1
            ? "+"
            : this.courtReservation.sets[i].toString();
      } else {
        this.points[i] = "+";
        this.courtReservation.sets[i] = -1;
      }
    }
    this.matchStarted = this.courtReservation.started;
  }

  private refreshAndFinalizeIfApproved() {
    if (!this.courtReservation) {
      return;
    }

    if (
      this.courtReservation.end_match_request_pending &&
      this.courtReservation.admin_approve_start &&
      !this.courtReservation.finished
    ) {
      this.endMatch();
      return;
    }

    if (!this.courtReservation.end_match_request_pending) {
      return;
    }

    if (!this.currentUser?.id) return;

    const start = new Date(2000, 0, 1);
    const end = new Date(2100, 0, 1);
    this.courtReservationService
      .getCourtReservationsForUser(this.currentUser.id, start, end)
      .subscribe((reservations: any[]) => {
        const match = reservations.find(
          (r: any) => r.id === this.courtReservation.id,
        );
        if (match) {
          const startDt = new Date(match.reservation_start_time);
          const players = (match.courtReservationPlayer || []).sort(
            (a: any, b: any) => a.position - b.position,
          );
          this.courtReservation = {
            ...match,
            date: startDt.toISOString().split("T")[0],
            time: startDt.getHours(),
            player_ids: players.map((p: any) => p.player_id),
            courtReservationPlayer: players,
          } as CourtReservation;
          this.saveMatch();
          this.initMatch();
          if (
            this.courtReservation.end_match_request_pending &&
            this.courtReservation.admin_approve_start &&
            !this.courtReservation.finished
          ) {
            this.endMatch();
          }
        }
      });
  }

  choosePlayer(i: number) {}

  playerName(i: number): string {
    const id = this.courtReservation.player_ids[i];
    if (this.currentUser?.id == id) return "Me";
    const player = this.courtReservation.courtReservationPlayer?.find(
      (p) => p.player_id === id,
    );
    if (player?.player?.full_name) return player.player.full_name;
    for (let user of this.users) {
      if (id == user.id) {
        return user.name;
      }
    }
    return "";
  }

  getDate(): string {
    let date = this.courtReservation.date;
    let dateSplit = date.split("-");

    return dateSplit[2] + "." + dateSplit[1] + "." + dateSplit[0] + ".";
  }

  getTime(): string {
    let start_time = this.courtReservation.time;
    let end_time = start_time + this.courtReservation.duration;

    let start_time_str = start_time.toString().padStart(2, "0") + ":00";
    let end_time_str = end_time.toString().padStart(2, "0") + ":00";

    return start_time_str + "-" + end_time_str;
  }

  getCourtNumber(): number {
    return 1;
  }

  getClub(): string {
    const courtId = this.courtReservation.court_id;
    if (this.courtReservation.court?.name) {
      return this.courtReservation.court.name;
    }
    for (let court of this.courts) {
      if (courtId == court.id) return court.name;
    }
    return "";
  }

  confirmMatch() {
    let startDate = new Date(Date.now());
    startDate.setDate(startDate.getDate() - 5);
    let endDate = new Date();
    endDate.setDate(endDate.getDate() + 15);
    if (!this.currentUser?.id) return;

    let courtReservations: CourtReservation[];
    this.courtReservationService
      .getCourtReservationsForUser(this.currentUser.id, startDate, endDate)
      .subscribe((response) => {
        courtReservations = response as CourtReservation[];
      });
    this.courtReservation.reservation_start_time = new Date(
      this.courtReservation.date,
    );
    this.courtReservation.reservation_start_time.setHours(
      this.courtReservation.time,
    );
    this.courtReservation.reservation_end_time = new Date(
      this.courtReservation.date,
    );
    this.courtReservation.reservation_end_time.setHours(
      this.courtReservation.time + this.courtReservation.duration,
    );
    this.courtReservation.user_id = this.currentUser.id;
    this.courtReservationService
      .addCourtReservation(this.courtReservation)
      .subscribe((response) => {
        this.courtReservation = response;
        courtReservations.push(this.courtReservation);
        this.localStorageService.setItem(
          "courtReservations",
          courtReservations,
        );
        this.localStorageService.remove("courtReservation");
      });
    this.routerService.navigateTo("confirmation/booked");
  }

  startMatch() {
    if (this.courtReservation.finished) return;
    this.courtReservationService
      .updateCourtReservation({
        ...this.courtReservation,
        started: true,
        note: this.courtReservation.note,
      })
      .subscribe(() => {
        this.courtReservation.started = true;
        this.matchStarted = true;
        this.saveMatch();
      });
  }

  saveMatch() {
    if (!this.yourMatchComponent) {
      this.localStorageService.setItem("match", this.courtReservation);

      for (let i = 0; i < this.courtReservations.length; i++) {
        if (
          this.courtReservation.court_id ==
            this.courtReservations[i].court_id &&
          this.courtReservation.court_num ==
            this.courtReservations[i].court_num &&
          this.courtReservation.time == this.courtReservations[i].time
        ) {
          this.courtReservations[i] = this.courtReservation;
        }
      }

      this.localStorageService.setItem(
        "courtReservations",
        this.courtReservations,
      );
    }
  }

  getPoints(i: number) {
    return this.courtReservation.sets[i] != -1
      ? this.courtReservation.sets[i]
      : "+";
  }

  onInputClick(i: number) {
    if (this.courtReservation.finished) return;
    this.points[i] = "";
  }

  onInputDone(i: number) {
    if (this.courtReservation.finished) return;

    const value = this.points[i].toString().trim();

    if (value === "" || value === "+") {
      this.points[i] = "+";
      this.courtReservation.sets[i] = -1;
      this.saveMatch();
      return;
    }

    const numValue = Number(value);
    if (
      !isNaN(numValue) &&
      numValue >= 0 &&
      numValue <= 7 &&
      Number.isInteger(numValue)
    ) {
      this.courtReservation.sets[i] = numValue;
      this.points[i] = numValue.toString();
      this.saveMatch();
    } else {
      this.points[i] = "+";
      this.courtReservation.sets[i] = -1;
      this.notificationService.warning(
        "Invalid Score",
        "Please enter a valid score between 0 and 7.",
      );
    }
  }

  private isMatchComplete(): boolean {
    const sets = this.courtReservation.sets;
    if (sets.some((s) => s === -1)) return false;

    const wins = [0, 0];
    for (let i = 0; i < 3; i++) {
      const t0 = sets[i];
      const t1 = sets[i + 3];
      if (t0 > t1) wins[0]++;
      else if (t1 > t0) wins[1]++;
    }

    return Math.max(wins[0], wins[1]) >= 2;
  }

  isMatchReadyToComplete(): boolean {
    return this.isMatchComplete();
  }

  private refreshNoteAnd(callback: () => void) {
    if (this.courtReservation.note) {
      callback();
      return;
    }
    if (!this.currentUser?.id) return;

    const start = new Date(2000, 0, 1);
    const end = new Date(2100, 0, 1);
    this.courtReservationService
      .getCourtReservationsForUser(this.currentUser.id, start, end)
      .subscribe((reservations: any[]) => {
        const match = reservations.find(
          (r: any) => r.id === this.courtReservation.id,
        );
        if (match) {
          this.courtReservation.note = match.note;
        }
        callback();
      });
  }

  saveAndEndMatch() {
    if (!this.isMatchComplete()) {
      this.notificationService.warning(
        "Incomplete Match",
        "Please complete all sets before ending the match. A team must win at least 2 sets.",
      );
      return;
    }

    if (!this.courtReservation.end_match_request_pending) {
      this.showEndMatchConfirmation();
      return;
    }

    this.refreshNoteAnd(() => this.endMatch());
  }

  private showEndMatchConfirmation(): void {
    const sets = this.courtReservation.sets;
    const wins = [0, 0];
    for (let i = 0; i < 3; i++) {
      const t0 = sets[i];
      const t1 = sets[i + 3];
      if (t0 > t1) wins[0]++;
      else if (t1 > t0) wins[1]++;
    }

    const winnerTeam = wins[0] > wins[1] ? 1 : 2;
    const scoreText = `${sets[0]}-${sets[3]}, ${sets[1]}-${sets[4]}, ${sets[2]}-${sets[5]}`;

    this.modalService
      .confirm({
        title: "Complete Match",
        message: `Final Score: ${scoreText}\nWinner: Team ${winnerTeam}\n\nThis will send the match for admin approval and points calculation.\n\nAre you sure you want to complete this match?`,
        confirmText: "Complete Match",
        cancelText: "Cancel",
      })
      .then((confirmed) => {
        if (confirmed) {
          this.submitEndMatch();
        }
      });
  }

  private submitEndMatch(): void {
    this.refreshNoteAnd(() => {
      this.courtReservationService
        .updateCourtReservation({
          ...this.courtReservation,
          sets: this.courtReservation.sets,
          end_match_request_pending: true,
          admin_approve_start: false,
          note: this.courtReservation.note,
        })
        .subscribe(() => {
          this.courtReservation.end_match_request_pending = true;
          this.courtReservation.admin_approve_start = false;
          this.saveMatch();
          this.notificationService.success(
            "Match Ended",
            "Match has been completed and sent for admin approval. Points will be awarded once approved.",
          );
        });
    });
  }

  getPlayerPointsCalculation(): any[] {
    if (!this.courtReservation.finished || !this.courtReservation.sets) {
      return [];
    }

    const sets = this.courtReservation.sets;
    const wins = [0, 0];
    for (let i = 0; i < 3; i++) {
      const t0 = sets[i];
      const t1 = sets[i + 3];
      if (t0 > t1) wins[0]++;
      else if (t1 > t0) wins[1]++;
    }

    if (Math.max(wins[0], wins[1]) < 2) {
      return [];
    }

    const winnerTeam = wins[0] > wins[1] ? 0 : 1;
    const playerIds = this.courtReservation.player_ids;
    const result = [];

    for (let i = 0; i < playerIds.length; i++) {
      const id = playerIds[i];
      const team = i % 2;
      const setsWon = wins[team];
      const setsLost = wins[team ^ 1];
      const isWinner = winnerTeam === team;
      let points = 15 + setsWon * 20 - setsLost * 5;
      if (isWinner) points += 20;

      result.push({
        playerId: id,
        playerName: this.playerName(i),
        setsWon,
        setsLost,
        isWinner,
        totalPoints: points,
      });
    }

    return result;
  }

  private endMatch() {
    if (this.courtReservation.finished) return;
    const sets = this.courtReservation.sets;
    if (sets.some((s) => s === -1)) return;

    const wins = [0, 0];
    for (let i = 0; i < 3; i++) {
      const t0 = sets[i];
      const t1 = sets[i + 3];
      if (t0 > t1) wins[0]++;
      else if (t1 > t0) wins[1]++;
    }
    if (Math.max(wins[0], wins[1]) < 2) return;

    const winnerTeam = wins[0] > wins[1] ? 0 : 1;
    const myId = this.currentUser?.id;
    const myTeam =
      this.courtReservation.player_ids[1] == myId ||
      (this.courtReservation.player_ids.length == 4 &&
        this.courtReservation.player_ids[3] == myId)
        ? 1
        : 0;

    if (!this.currentUser) return;

    const myPoints = this.calculatePoints(myTeam, wins, winnerTeam);
    const updatedCurrentUser = {
      ...this.currentUser,
      pp_points: (this.currentUser.pp_points || 0) + myPoints,
    } as User;

    this.localStorageService.setItem("matchPoints", myPoints);
    this.localStorageService.setItem("currentUser", updatedCurrentUser);
    for (let i = 0; i < this.users.length; i++) {
      if (this.users[i].id === myId) {
        this.users[i] = updatedCurrentUser;
        this.localStorageService.setItem("users", this.users);
        break;
      }
    }

    this.courtReservationService
      .updateCourtReservation({
        ...this.courtReservation,
        finished: true,
        sets: this.courtReservation.sets,
        end_match_request_pending: false,
        note: this.courtReservation.note,
      })
      .subscribe(() => {
        this.courtReservation.finished = true;
        this.courtReservation.end_match_request_pending = false;
        this.saveMatch();
        this.awardPoints(winnerTeam, wins);
        this.routerService.navigateTo(
          winnerTeam === myTeam ? "confirmation/win" : "confirmation/loss",
        );
      });
  }

  private calculatePoints(
    team: number,
    wins: number[],
    winnerTeam: number,
  ): number {
    const setsWon = wins[team];
    const setsLost = wins[team ^ 1];
    let points = 15 + setsWon * 20 - setsLost * 5;
    if (winnerTeam === team) points += 20;
    return points;
  }

  private awardPoints(winnerTeam: number, wins: number[]) {
    const playerIds = this.courtReservation.player_ids;
    for (let i = 0; i < playerIds.length; i++) {
      const id = playerIds[i];
      const team = i % 2;
      const setsWon = wins[team];
      const setsLost = wins[team ^ 1];
      let points = 15 + setsWon * 20 - setsLost * 5;
      if (winnerTeam === team) points += 20;

      const winsChange = winnerTeam === team ? 1 : 0;
      const losesChange = winnerTeam === team ? 0 : 1;

      console.log(
        `Updating stats for player ${id}: points=${points}, wins=${winsChange}, loses=${losesChange}`,
      );

      this.playerService
        .updatePlayerStats({
          id,
          pp_point_change: points,
          wins_change: winsChange,
          loses_change: losesChange,
          point_change: 0,
        })
        .subscribe({
          next: (response) => {
            console.log(
              `Successfully updated stats for player ${id}:`,
              response,
            );
            if (id === this.currentUser?.id) {
              const updated = {
                ...this.currentUser,
                pp_points: (this.currentUser.pp_points || 0) + points,
                wins: (this.currentUser.wins || 0) + winsChange,
                loses: (this.currentUser.loses || 0) + losesChange,
              } as User;
              this.localStorageService.setItem("matchPoints", points);
              this.localStorageService.setItem("currentUser", updated);
              this.currentUser = updated;
              this.notificationService.success(
                "Points Updated",
                `You earned ${points} PP points!`,
              );
            }
            for (let j = 0; j < this.users.length; j++) {
              if (this.users[j].id === id) {
                this.users[j] = {
                  ...this.users[j],
                  pp_points: (this.users[j].pp_points || 0) + points,
                  wins: (this.users[j].wins || 0) + winsChange,
                  loses: (this.users[j].loses || 0) + losesChange,
                } as User;
                this.localStorageService.setItem("users", this.users);
                break;
              }
            }
          },
          error: (error) => {
            console.error(`Failed to update stats for player ${id}:`, error);
            this.notificationService.error(
              "Points Update Failed",
              `Failed to update points for player ${id}. Please contact support.`,
            );
          },
        });
    }
  }

  private checkForAdminApproval(): void {
    if (!this.currentUser?.id) return;

    console.log(
      `Checking for admin approval for match ${this.courtReservation.id}`,
    );

    const start = new Date(2000, 0, 1);
    const end = new Date(2100, 0, 1);
    this.courtReservationService
      .getCourtReservationsForUser(this.currentUser.id, start, end)
      .subscribe({
        next: (reservations: any[]) => {
          const match = reservations.find(
            (r: any) => r.id === this.courtReservation.id,
          );
          if (match) {
            console.log(`Found updated match data:`, match);
            const startDt = new Date(match.reservation_start_time);
            const players = (match.courtReservationPlayer || []).sort(
              (a: any, b: any) => a.position - b.position,
            );
            this.courtReservation = {
              ...match,
              date: startDt.toISOString().split("T")[0],
              time: startDt.getHours(),
              player_ids: players.map((p: any) => p.player_id),
              courtReservationPlayer: players,
            } as CourtReservation;

            if (this.courtReservation.admin_approve_start) {
              this.notificationService.success(
                "Approval Found",
                "Admin approval has been granted! You can now save points.",
              );
            } else {
              this.notificationService.info(
                "Still Pending",
                "Admin approval is still pending. Please wait for club staff to approve the match.",
              );
            }

            this.saveMatch();
            this.initMatch();
          } else {
            console.error(
              `Match ${this.courtReservation.id} not found in user reservations`,
            );
            this.notificationService.error(
              "Error",
              "Could not refresh match data. Please try again.",
            );
          }
        },
        error: (error) => {
          console.error(`Failed to check for admin approval:`, error);
          this.notificationService.error(
            "Error",
            "Failed to check approval status. Please try again.",
          );
        },
      });
  }
}
